import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const SchoolMap = ({ address, mapsUrl = null, embedUrl: customEmbedUrl = null }) => {
    // Priority: customEmbedUrl > mapsUrl > address
    let embedUrl, searchUrl;

    if (customEmbedUrl) {
        // Use the provided custom embed URL directly
        embedUrl = customEmbedUrl;
        // Use mapsUrl if available, otherwise fall back to address search
        searchUrl = mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    } else if (mapsUrl) {
        // Use the provided Google Maps URL for external link
        searchUrl = mapsUrl;

        // For embed, use the address since shortened URLs can't be embedded
        const encodedAddress = encodeURIComponent(address);
        embedUrl = `https://maps.google.com/maps?q=${encodedAddress}&output=embed`;
    } else {
        // Encode address for URL
        const encodedAddress = encodeURIComponent(address);

        // Google Maps embed URL
        embedUrl = `https://maps.google.com/maps?q=${encodedAddress}&output=embed`;

        // Google Maps search URL (opens in new tab)
        searchUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    const handleMapClick = () => {
        window.open(searchUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full mt-6"
        >
            <div className="relative w-full rounded-xl overflow-hidden shadow-medium hover:shadow-strong transition-all duration-300 group">
                {/* Map Container with aspect ratio */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    {/* Embedded Google Map */}
                    <iframe
                        src={embedUrl}
                        className="absolute top-0 left-0 w-full h-full border-0"
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map of ${address}`}
                    />

                    {/* Clickable Overlay */}
                    <button
                        onClick={handleMapClick}
                        className="absolute inset-0 bg-transparent hover:bg-primary/5 transition-all duration-300 cursor-pointer z-10 flex items-center justify-center"
                        aria-label="Open in Google Maps"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileHover={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                            className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-strong flex items-center gap-2 font-medium"
                        >
                            <ExternalLink className="w-4 h-4" />
                            <span className="text-sm">Open in Google Maps</span>
                        </motion.div>
                    </button>
                </div>

                {/* Bottom gradient overlay for better visual */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-foreground/10 to-transparent pointer-events-none" />
            </div>

            {/* Helper text */}
            <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Click on the map to open in Google Maps
            </p>
        </motion.div>
    );
};

export default SchoolMap;
